import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Input from '../../../../components/Input';
import CustomizedSwitch from '../../../../components/Switch';
import Button from '../../../../components/Button';
import Toast from '../../../../components/Toast';
import Spinner from '../../../../components/Spinner';
import MasterLookupService from '../../../../services/master-lookup.service';
import { Modules, AuthRoutes } from '../../../../enums';

const UTC_OFFSET_REGEX = /^[+-]\d{2}:\d{2}$/;

interface TimezoneFormData {
  tz_code: string;
  tz_name: string;
  utc_offset: string;
  status: 'active' | 'inactive';
  notes: string;
}

const initialForm: TimezoneFormData = {
  tz_code: '',
  tz_name: '',
  utc_offset: '',
  status: 'active',
  notes: '',
};

const TimezoneForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const edit = !!id;

  const [formData, setFormData] = useState<TimezoneFormData>(initialForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(edit);

  useEffect(() => {
    if (!id) {
      setFormData(initialForm);
      return;
    }
    setFetching(true);
    MasterLookupService.get('timezones', id)
      .then((response: any) => {
        const row = response?.data ?? {};
        setFormData({
          tz_code: row.tz_code ?? '',
          tz_name: row.tz_name ?? '',
          utc_offset: row.utc_offset ?? '',
          status: row.status ?? 'active',
          notes: row.notes ?? '',
        });
      })
      .finally(() => setFetching(false));
  }, [id]);

  const validate = useCallback(
    (data: TimezoneFormData) => {
      const nextErrors: Record<string, string> = {};
      if (!data.tz_code.trim()) nextErrors.tz_code = t('masters.timezones.form.pageTexts.fields.tzCode.errors.required');
      if (!data.tz_name.trim()) nextErrors.tz_name = t('masters.timezones.form.pageTexts.fields.tzName.errors.required');
      if (!UTC_OFFSET_REGEX.test(data.utc_offset)) {
        nextErrors.utc_offset = t('masters.timezones.form.pageTexts.fields.utcOffset.errors.invalid');
      }
      return nextErrors;
    },
    [t]
  );

  const handleChange = useCallback((e: any, name: string) => {
    const value = name === 'status' ? (e.target.checked ? 'active' : 'inactive') : e.target.value;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      e && e.preventDefault();
      const nextErrors = validate(formData);
      setErrors(nextErrors);
      if (Object.keys(nextErrors).length > 0) return;

      setLoading(true);
      try {
        const payload = { ...formData };
        if (edit && id) {
          await MasterLookupService.update('timezones', id, payload);
          toast.success(<Toast message={t('masters.timezones.form.pageTexts.editSuccess')} />, { icon: false });
        } else {
          await MasterLookupService.create('timezones', payload);
          toast.success(<Toast message={t('masters.timezones.form.pageTexts.addSuccess')} />, { icon: false });
        }
        navigate(`${Modules.masters}${AuthRoutes.timezones}`);
      } catch (err) {
        setLoading(false);
        return;
      }
      setLoading(false);
    },
    [formData, edit, id, navigate, validate, t]
  );

  if (fetching) {
    return (
      <div className="card-header text-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="card mb-6">
      <h5 className="card-header">
        {edit ? t('masters.timezones.form.pageTexts.editTitle') : t('masters.timezones.form.pageTexts.addTitle')}
      </h5>
      <hr className="m-0" />
      <form className="card-body row g-4" onSubmit={handleSubmit}>
        <div className="col-md-4">
          <Input
            id="tz_code"
            name="tz_code"
            label={t('masters.timezones.form.pageTexts.fields.tzCode.label')}
            placeholder={t('masters.timezones.form.pageTexts.fields.tzCode.placeholder')}
            required
            value={formData.tz_code}
            error={!!errors.tz_code}
            errorMessage={errors.tz_code}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-4">
          <Input
            id="tz_name"
            name="tz_name"
            label={t('masters.timezones.form.pageTexts.fields.tzName.label')}
            placeholder={t('masters.timezones.form.pageTexts.fields.tzName.placeholder')}
            required
            value={formData.tz_name}
            error={!!errors.tz_name}
            errorMessage={errors.tz_name}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-4">
          <Input
            id="utc_offset"
            name="utc_offset"
            label={t('masters.timezones.form.pageTexts.fields.utcOffset.label')}
            placeholder="+05:30"
            required
            value={formData.utc_offset}
            error={!!errors.utc_offset}
            errorMessage={errors.utc_offset}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-3">
          <CustomizedSwitch
            label={t('masters.timezones.form.pageTexts.fields.status.label')}
            name="status"
            checked={formData.status === 'active'}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-8">
          <Input
            id="notes"
            name="notes"
            label={t('masters.timezones.form.pageTexts.fields.notes.label')}
            placeholder={t('masters.timezones.form.pageTexts.fields.notes.placeholder')}
            value={formData.notes}
            onChange={handleChange}
          />
        </div>
        <div className="col-12 d-flex gap-2 justify-content-end">
          <Button
            type="button"
            className="btn btn-outline-secondary"
            text={t('masters.timezones.form.pageTexts.buttons.cancel')}
            onClick={() => navigate(`${Modules.masters}${AuthRoutes.timezones}`)}
          />
          <Button
            type="submit"
            className="btn btn-primary"
            text={t('masters.timezones.form.pageTexts.buttons.save')}
            loading={loading}
          />
        </div>
      </form>
    </div>
  );
};

export default TimezoneForm;
